import { trackVideoEvents } from '@utils/gtm/ga4/trackVideoEvents';
import { useEffect } from 'react';
import throttle from 'lodash.throttle';

export const useVideoTracking = (
  videoRef: React.RefObject<HTMLVideoElement | null>,
  videoTitle?: string
) => {
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const progressMarkers = {
      25: false,
      50: false,
      75: false,
      90: false,
    };
    let hasStarted = false;
    let lastUpdateTime = 0;

    const handleProgress = throttle(
      (currentTime: number, duration: number) => {
        const now = Date.now();
        if (now - lastUpdateTime < 2000) return;

        lastUpdateTime = now;
        const pct = Math.floor((100 * currentTime) / duration);

        (
          Object.keys(progressMarkers) as unknown as Array<
            keyof typeof progressMarkers
          >
        ).forEach((marker) => {
          const percent = Number(marker);
          if (pct >= percent && !progressMarkers[marker]) {
            progressMarkers[marker] = true;
            trackVideoEvents({
              event: {
                eventName: 'video_progress',
                videoPercent: percent,
                videoTitle: videoTitle || video.ariaLabel,
                videoDuration: duration.toString(),
                videoUrl: video.currentSrc,
                videoProvider: 'Prismic',
                interactionLocation: 'embedded',
                type: 'PLP',
                path: window.location.pathname,
              },
            });
          }
        });
      },
      2000,
      { trailing: false }
    );

    const handleVideoEvent = (e: Event) => {
      const videoElement = e.target as HTMLVideoElement;
      const videoUrl = videoElement.currentSrc;
      const videoDuration = videoElement.duration;

      switch (e.type) {
        case 'timeupdate':
          if (hasStarted) {
            handleProgress(video.currentTime, video.duration);
          }
          break;

        case 'play':
          if (!hasStarted) {
            hasStarted = true;
            lastUpdateTime = Date.now();
            trackVideoEvents({
              event: {
                eventName: 'video_start',
                videoPercent: 0,
                videoTitle: videoTitle || videoElement.ariaLabel,
                videoDuration: videoDuration.toString(),
                videoUrl: videoUrl,
                videoProvider: 'Prismic',
                interactionLocation: 'embedded',
                type: 'PLP',
                path: window.location.pathname,
              },
            });
          }
          break;

        case 'ended':
          handleProgress.cancel();
          trackVideoEvents({
            event: {
              eventName: 'video_complete',
              videoPercent: 100,
              videoTitle: videoTitle || videoElement.ariaLabel,
              videoDuration: videoDuration.toString(),
              videoUrl: videoUrl,
              videoProvider: 'Prismic',
              interactionLocation: 'embedded',
              type: 'PLP',
              path: window.location.pathname,
            },
          });
          break;
      }
    };

    video.addEventListener('play', handleVideoEvent);
    video.addEventListener('ended', handleVideoEvent);
    video.addEventListener('timeupdate', handleVideoEvent);

    return () => {
      video.removeEventListener('play', handleVideoEvent);
      video.removeEventListener('ended', handleVideoEvent);
      video.removeEventListener('timeupdate', handleVideoEvent);
    };
  }, [videoRef, videoTitle]);
};
